import React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Download,
  Calendar,
  User,
  Building,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Info,
  View,
  ViewIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { complianceService } from "../../services/api";
import {
  getStatusBadge,
  getVerificationStatusBadge,
  formatDate,
  formatDateTime,
  getFrequencyLabel,
  getAttachmentUrl,
} from "../../utils/helpers";
import LoadingSpinner from "../Common/LoadingSpinner";

const ComplianceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // const queryClient = useQueryClient();
  const { user } = useAuth();

  // Fetch compliance details
  const {
    data: compl,
    isLoading,
    error,
  } = useQuery(
    ["compliance", id],
    () => complianceService.getComplianceById(id),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const compliance = compl?.data;
  // Delete compliance mutation
  const deleteMutation = useMutation(
    (id) => complianceService.deleteCompliance(id),
    {
      onSuccess: () => {
        toast.success("Compliance deleted successfully");
        navigate("/compliances");
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message || "Failed to delete compliance"
        );
      },
    }
  );

  // Handle delete
  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this compliance?")) {
      deleteMutation.mutate(id);
    }
  };

  // Handle download document
  const handleDownload = () => {
    if (compliance?.cloudinaryURL) {
      window.open(getAttachmentUrl(compliance.cloudinaryURL), "_blank");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading compliance details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Error loading compliance
          </h3>
          <p className="mt-1 text-sm text-gray-500">{error.message}</p>
          <div className="mt-6">
            <Link
              to="/compliances"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Compliances
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!compliance) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Info className="mx-auto h-12 w-12 text-blue-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Compliance not found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            The compliance you're looking for doesn't exist.
          </p>
          <div className="mt-6">
            <Link
              to="/compliances"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Compliances
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center">
          <Link
            to="/compliances"
            className="mr-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {compliance.title}
            </h1>
            {/* <p className="mt-1 text-sm text-gray-500">
              Compliance ID: {compliance._id}
            </p> */}
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
          {(user?.role === "admin" ||
            (user?.role === "employee" &&
              compliance.assignedTo?._id === user._id)) && (
              <Link
                to={`/compliances/${compliance._id}/edit`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Link>
            )}
          {user?.role === "admin" && (
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {getStatusBadge(compliance.status)}
          {compliance.verificationStatus && (
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-2">Verification:</span>
              {getVerificationStatusBadge(compliance.verificationStatus)}
            </div>
          )}
        </div>
        <div className="text-sm text-gray-500">
          Created: {formatDateTime(compliance.createdAt)}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Basic Information
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  License
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {compliance.license}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Frequency
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {getFrequencyLabel(compliance.frequency)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Due Date
                  </label>
                  <div className="mt-1 flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {formatDate(compliance.dueDate)}
                    </span>
                  </div>
                </div>
              </div>

              {compliance.reason && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Reason
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {compliance.reason}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Assignment Information */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Assignment</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Assigned To
                  </label>
                  <div className="mt-1 flex items-center">
                    <User className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {compliance.assignedTo && compliance.assignedTo.length > 0
                        ? compliance.assignedTo
                          .map((user) => user.name)
                          .join(", ")
                        : "Unassigned"}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Plant
                  </label>
                  <div className="mt-1 flex items-center">
                    <Building className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {compliance.plant?.name || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Document (visible to Admin, Super Admin, and Assigned Employee) */}
          {(user?.role === 'superAdmin' || user?.role === 'admin' || (user?.role === 'employee' && compliance.assignedTo?.some(u => u._id === user._id))) && compliance.cloudinaryURL && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Document</h3>
              </div>
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-sm text-gray-900">
                      Uploaded Document
                    </span>
                  </div>
                  <button
                    onClick={() => window.open(getAttachmentUrl(compliance.cloudinaryURL), '_blank')}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >

                    View Document
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Additional Info */}
        <div className="space-y-6 h-fit">
          {/* Status Timeline */}
          <div className="bg-white shadow rounded-lg h-fit">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Status Timeline
              </h3>
            </div>
            <div className="px-6 py-4">
              <div className="flow-root overflow-hidden">
                <ul className="-mb-8">
                  {/* Step 1: Compliance Assigned (for employees) or Created (for admins) */}
                  <li>
                    <div className="relative pb-8">
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                      <div className="relative flex space-x-3">
                        <div className="flex-shrink-0">
                          <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                            <FileText className="h-5 w-5 text-white" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-1 sm:space-y-0">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-500">
                                {user?.role === "employee" ? (
                                  <>
                                    Compliance{" "}
                                    <span className="font-medium text-gray-900">
                                      assigned to you
                                    </span>
                                    {compliance.submittedBy && (
                                      <span className="text-xs text-gray-400 block">
                                        by {compliance.submittedBy.name || 'Admin'}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    Compliance{" "}
                                    <span className="font-medium text-gray-900">
                                      created
                                    </span>
                                    {compliance.submittedBy && (
                                      <span className="text-xs text-gray-400 block">
                                        by {compliance.submittedBy.name || 'Admin'}
                                      </span>
                                    )}
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="text-sm text-gray-500 flex-shrink-0">
                              <span className="block sm:inline">{formatDateTime(compliance.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>

                  {/* Step 2: Send for Verification */}
                  {compliance.isSendForVerification && (
                    <li>
                      <div className="relative pb-8">
                        <span
                          className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                          aria-hidden="true"
                        />
                        <div className="relative flex space-x-3">
                          <div className="flex-shrink-0">
                            <span className="h-8 w-8 rounded-full bg-yellow-500 flex items-center justify-center ring-8 ring-white">
                              <Clock className="h-5 w-5 text-white" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-1 sm:space-y-0">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">
                                  {user?.role === "employee" ? (
                                    <>
                                      You sent for{" "}
                                      <span className="font-medium text-gray-900">
                                        verification
                                      </span>
                                      <span className="text-xs text-gray-400 block">
                                        Awaiting admin review
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      Sent for{" "}
                                      <span className="font-medium text-gray-900">
                                        verification
                                      </span>
                                      <span className="text-xs text-gray-400 block">
                                        Awaiting admin review
                                      </span>
                                    </>
                                  )}
                                </p>
                              </div>
                              <div className="text-sm text-gray-500 flex-shrink-0">
                                <span className="block sm:inline">{formatDateTime(compliance.updatedAt)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  )}

                  {/* Step 3: Compliance Verified */}
                  {compliance.verificationStatus === 'approved' && (
                    <li>
                      <div className="relative pb-8">
                        <span
                          className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                          aria-hidden="true"
                        />
                        <div className="relative flex space-x-3">
                          <div className="flex-shrink-0">
                            <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                              <CheckCircle className="h-5 w-5 text-white" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-1 sm:space-y-0">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">
                                  {user?.role === "employee" ? (
                                    <>
                                      Compliance{" "}
                                      <span className="font-medium text-green-600">
                                        verified
                                      </span>
                                      {compliance.verifiedBy && (
                                        <span className="text-xs text-gray-400 block">
                                          by {compliance.verifiedBy.name || 'Admin'}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      Verification{" "}
                                      <span className="font-medium text-green-600">
                                        approved
                                      </span>
                                      {compliance.verifiedBy && (
                                        <span className="text-xs text-gray-400 block">
                                          by {compliance.verifiedBy.name || 'Admin'}
                                        </span>
                                      )}
                                    </>
                                  )}
                                </p>
                              </div>
                              <div className="text-sm text-gray-500 flex-shrink-0">
                                <span className="block sm:inline">{formatDateTime(compliance.verificationDate)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  )}

                  {/* Step 4: Compliance Completed */}
                  {compliance.status === 'completed' && (
                    <li>
                      <div className="relative">
                        <div className="relative flex space-x-3">
                          <div className="flex-shrink-0">
                            <span className="h-8 w-8 rounded-full bg-green-600 flex items-center justify-center ring-8 ring-white">
                              <CheckCircle className="h-5 w-5 text-white" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-1 sm:space-y-0">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">
                                  Compliance{" "}
                                  <span className="font-medium text-green-600">
                                    marked completed
                                  </span>
                                  <span className="text-xs text-gray-400 block">
                                    All requirements fulfilled
                                  </span>
                                </p>
                              </div>
                              <div className="text-sm text-gray-500 flex-shrink-0">
                                <span className="block sm:inline">{formatDateTime(compliance.updatedAt)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  )}

                </ul>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white shadow rounded-lg h-fit">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Quick Actions
              </h3>
            </div>
            <div className="px-6 py-4 space-y-3">
              {(user?.role === "admin" ||
                (user?.role === "employee" &&
                  compliance.assignedTo?._id === user._id)) && (
                  <Link
                    to={`/compliances/${compliance._id}/edit`}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Compliance
                  </Link>
                )}
              {compliance.cloudinaryURL && (
                <button
                  onClick={handleDownload}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  {/* <Download className="h-4 w-4 mr-2" /> */}
                  View Document
                </button>
              )}
              {user?.role === "admin" && (
                <button
                  onClick={handleDelete}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Compliance
                </button>
              )}
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-white shadow rounded-lg h-fit">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Additional Information
              </h3>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Created by:</span>
                <span className="text-sm text-gray-900">
                  {compliance.createdBy?.name || "System"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Last updated:</span>
                <span className="text-sm text-gray-900">
                  {formatDateTime(compliance.updatedAt)}
                </span>
              </div>
              {compliance.verificationStatus && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Verification:</span>
                  <span className="text-sm text-gray-900">
                    {compliance.verificationStatus}
                  </span>
                </div>
              )}
              {/* Show internal remark only to super admin */}
              {user?.role === 'superAdmin' && compliance.reason && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Internal Remark:</span>
                  <span className="text-sm text-gray-900 break-all text-right">{compliance.reason}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceDetail;
